import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/frontend/components/ui/dialog";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Label } from "@/frontend/components/ui/label";
import { Copy, Check } from "lucide-react";
import { useToast } from "@/backend/hooks/use-toast";

interface StudentCredential {
  name: string;
  email: string;
  password: string;
}

interface StudentCredentialsModalProps {
  open: boolean;
  onClose: () => void;
  students: StudentCredential[];
}

export const StudentCredentialsModal = ({
  open,
  onClose,
  students,
}: StudentCredentialsModalProps) => {
  const { toast } = useToast();
  // Track copy state per student index and field: { [index]: { email, password } }
  const [copied, setCopied] = useState<Record<number, { email?: boolean; password?: boolean }>>({});

  const copyToClipboard = async (text: string, index: number, field: "email" | "password") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied((prev) => ({
        ...prev,
        [index]: { ...prev[index], [field]: true },
      }));
      setTimeout(() => {
        setCopied((prev) => ({
          ...prev,
          [index]: { ...prev[index], [field]: false },
        }));
      }, 2000);
      toast({
        title: "Copied!",
        description: `${field === "email" ? "Email" : "Password"} copied to clipboard`,
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {students.length > 1 ? "Student Accounts Created" : "Student Account Created"}
          </DialogTitle>
          <DialogDescription>
            Share these credentials with the student{students.length > 1 ? "s" : ""}. This is the only time the passwords will be shown.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {students.map((student, i) => (
            <div key={i}>
              {students.length > 1 && (
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  {student.name}
                </p>
              )}

              <div className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor={`email-${i}`}>Email</Label>
                  <div className="flex gap-2">
                    <Input
                      id={`email-${i}`}
                      value={student.email}
                      readOnly
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      onClick={() => copyToClipboard(student.email, i, "email")}
                    >
                      {copied[i]?.email ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor={`password-${i}`}>Password</Label>
                  <div className="flex gap-2">
                    <Input
                      id={`password-${i}`}
                      value={student.password}
                      readOnly
                      className="flex-1 font-mono"
                    />
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      onClick={() => copyToClipboard(student.password, i, "password")}
                    >
                      {copied[i]?.password ? (
                        <Check className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {i < students.length - 1 && <hr className="mt-4" />}
            </div>
          ))}

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <p className="text-sm text-amber-800">
              <strong>Important:</strong> Save these credentials securely. The passwords cannot be recovered later.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
